import { getAxiosError } from "../../../../../../utils/axiosError";
import { type AppDispatch } from "../../../../../Application/globaltypes/redux";
import { sendExportRequest } from "../../../../Common/utils/performanceUtils";
import { type EmailActivity } from "../../EmailDetails/Performance/types";
import emailsDataService from "../../services/emailsDataService";
import {
  reqSendDensity,
  setSendDensity,
  setSendDensityError,
  reqEngagementData,
  setEngagementData,
  setEngagementError,
  reqSendsDetails,
  setSendsDetails,
  setSendsDetailsError,
  reqSendSessions,
  setSendSessions,
  setSendSessionsError,
} from "../slices/emailPerformanceSlice";
import { type AccountTypePerformanceRequestFilterParams } from "features/Library/Common/models";

type EmailInformation = {
  emailId: number;
  sendSessionId?: number;
};

export const fetchEmailSendDensity = (
  emailInfo: EmailInformation,
  filterParams: AccountTypePerformanceRequestFilterParams,
) => {
  return async (dispatch: AppDispatch) => {
    dispatch(reqSendDensity());
    try {
      const { data } = await emailsDataService.getEmailLineChart(
        emailInfo.emailId,
        emailInfo.sendSessionId,
        filterParams,
      );
      dispatch(setSendDensity({ data }));
    } catch (err) {
      // Cancellation token error
      if (err.code !== "ERR_CANCELED") {
        const errorMessage = getAxiosError(err);
        dispatch(setSendDensityError({ errorMessage }));
      }
    }
  };
};

export const fetchEmailSendFunnel = (
  emailInfo: EmailInformation,
  filterParams: AccountTypePerformanceRequestFilterParams,
) => {
  return async (dispatch: AppDispatch) => {
    dispatch(reqEngagementData());
    try {
      const missingRates = (
        await emailsDataService.getEmailEngagement(emailInfo.emailId, emailInfo.sendSessionId, filterParams)
      ).data;
      const data: EmailActivity = {
        ...missingRates,
        OpenRate: 0,
        ClickRate: 0,
      };
      dispatch(setEngagementData({ data }));
    } catch (err) {
      if (err.code !== "ERR_CANCELED") {
        const errorMessage = getAxiosError(err);
        dispatch(setEngagementError({ errorMessage }));
      }
    }
  };
};

export const fetchEmailSendSessions = (emailId: number, filterParams: AccountTypePerformanceRequestFilterParams) => {
  return async (dispatch: AppDispatch) => {
    dispatch(reqSendSessions());
    try {
      const { data } = await emailsDataService.getEmailSendSessions(emailId, filterParams);
      dispatch(
        setSendSessions({
          data: data.map((d) => ({
            ...d,
            id: d.SendSessionId,
            SendSession: d.SendSession ?? "-",
          })),
        }),
      );
    } catch (err) {
      if (err.code !== "ERR_CANCELED") {
        const errorMessage = getAxiosError(err);
        dispatch(setSendSessionsError({ errorMessage }));
      }
    }
  };
};

export const fetchEmailSendDetails = (
  emailId: number,
  sendSessionId: number,
  filterParams: AccountTypePerformanceRequestFilterParams,
) => {
  return async (dispatch: AppDispatch) => {
    dispatch(reqSendsDetails());

    try {
      const results = (await emailsDataService.getEmailSendDetailsV3(emailId, sendSessionId, filterParams)).data;

      const resultsWithId = results.map((item, index) => {
        return {
          ...item,
          id: index.toString(),
        };
      });

      dispatch(
        setSendsDetails({
          data: resultsWithId,
        }),
      );
    } catch (err) {
      const errorMessage = getAxiosError(err);
      dispatch(setSendsDetailsError({ errorMessage }));
    }
  };
};

export const handleCommunicationsExport = (
  emailId: number,
  filterParams: AccountTypePerformanceRequestFilterParams,
  emailTitle: string,
) => {
  let abortController = new AbortController();
  return sendExportRequest(
    (abortController) => emailsDataService.getExport(emailId, filterParams),
    emailTitle,
    abortController,
  );
};

export const handleSendSessionExport = (
  sendSessionId: number,
  filterParams: AccountTypePerformanceRequestFilterParams,
  fileName: string,
) => {
  let abortController = new AbortController();
  return sendExportRequest(
    (abortController) => emailsDataService.getSendSessionExport(sendSessionId, filterParams),
    fileName,
    abortController,
  );
};
